<template>
  <div>
    <RctNavbar :links="navLinks" logo-href="https://ridgecodetech.com">
    </RctNavbar>

    <!-- HERO -->
    <section class="hero">
      <div class="hero-content">
        <h1 class="hero-headline">Real engineering experience,<br><em>before graduation.</em></h1>
        <p class="hero-sub">A structured internship program for software engineering students — real client work, open source contributions, and practice projects that build a resume worth reading.</p>
        <div class="hero-actions">
          <RctButton href="#notify">Register Interest</RctButton>
          <RctButton variant="ghost" href="#how-it-works">How It Works →</RctButton>
        </div>
      </div>
      <div class="ridge-divider">
        <svg viewBox="0 0 1130 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="80" width="1130" height="40" fill="#f5f8ff"/>
          <path d="M0,120 L0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92 L1130,120 Z" fill="#f5f8ff"/>
          <path d="M0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92" fill="none" stroke="#becdea" stroke-width="1.5" opacity="0.6"/>
        </svg>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="how-section" id="how-it-works">
      <div class="section-inner">
        <p class="section-label reveal">The Program</p>
        <h2 class="section-heading reveal reveal-delay-1">Structured work.<br>Real outcomes.</h2>
        <div class="steps-grid">
          <div class="step reveal reveal-delay-1">
            <div class="step-number">01</div>
            <h3 class="step-title">Join the Program</h3>
            <p class="step-desc">Your institution partners with RidgeCodeTech, or you apply directly as a student. Institutional partners get access to the full project mix and structured curriculum.</p>
          </div>
          <div class="step reveal reveal-delay-2">
            <div class="step-number">02</div>
            <h3 class="step-title">Get Assigned Work</h3>
            <p class="step-desc">Interns receive tasks matched to their skill level — real client projects, open source contributions, or structured practice builds. Everything ships or gets reviewed.</p>
          </div>
          <div class="step reveal reveal-delay-3">
            <div class="step-number">03</div>
            <h3 class="step-title">Build Your Portfolio</h3>
            <p class="step-desc">Completed work goes on your resume. Real commits, shipped features, and client-ready deliverables — the kind of experience employers look for before day one.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- WHAT YOU WORK ON -->
    <section class="work-section" id="projects">
      <div class="section-inner">
        <p class="section-label reveal">Project Types</p>
        <h2 class="section-heading reveal reveal-delay-1">Work that actually matters.</h2>
        <p class="section-body reveal reveal-delay-2">Institutional partners get access to all three. Individual applicants work on a curated subset based on availability.</p>
        <div class="work-grid reveal reveal-delay-3">
          <div class="work-card">
            <div class="work-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="8" width="28" height="24" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M6 16h28" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M13 12h2M18 12h2" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M12 24l4 4 8-8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="work-tag institutional-tag">Institutional</div>
            <h3 class="work-title">Real Client Projects</h3>
            <p class="work-desc">Contribute to actual client work under supervision — feature builds, bug fixes, and tooling. Code that ships to production and belongs on a resume.</p>
          </div>
          <div class="work-card">
            <div class="work-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="12" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M16 16 C16 14 24 14 24 18 C24 21 20 22 20 24" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="20" cy="28" r="1.5" fill="#7a96c1"/>
              </svg>
            </div>
            <div class="work-tag both-tag">All Students</div>
            <h3 class="work-title">Practice Projects</h3>
            <p class="work-desc">Structured assignments designed to build specific skills — APIs, data pipelines, frontend components. Reviewed and graded for quality, not just completion.</p>
          </div>
          <div class="work-card">
            <div class="work-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="12" r="4" stroke="#7a96c1" stroke-width="1.5"/>
                <circle cx="8" cy="30" r="4" stroke="#7a96c1" stroke-width="1.5"/>
                <circle cx="32" cy="30" r="4" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M20 16v6M20 22l-8 4M20 22l8 4" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="work-tag institutional-tag">Institutional</div>
            <h3 class="work-title">Open Source Contributions</h3>
            <p class="work-desc">Contribute to real open source projects with guidance. Learn how to navigate public codebases, write useful pull requests, and work with maintainers.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- TWO PATHS -->
    <section class="paths-section" id="paths">
      <div class="section-inner">
        <p class="section-label reveal">Enrollment Options</p>
        <h2 class="section-heading reveal reveal-delay-1">Two ways to join.</h2>
        <div class="paths-grid reveal reveal-delay-2">
          <div class="path-card path-institutional">
            <div class="path-recommended">Recommended</div>
            <h3 class="path-title">Institutional Partner</h3>
            <p class="path-audience">For colleges &amp; universities</p>
            <p class="path-desc">Your CS department enrolls as a partner. Students gain access to the full program — all three project types, structured curriculum, and direct integration with your course requirements.</p>
            <ul class="path-features">
              <li>Full project mix — client, practice, open source</li>
              <li>Department-level enrollment and management</li>
              <li>Structured curriculum aligned to CS coursework</li>
              <li>Progress reporting for department records</li>
              <li>Scalable to any cohort size</li>
            </ul>
            <RctButton href="#notify" class="path-cta">Inquire About Partnership</RctButton>
          </div>
          <div class="path-card path-individual">
            <h3 class="path-title">Individual Student</h3>
            <p class="path-audience">For students applying directly</p>
            <p class="path-desc">Apply on your own if your institution isn't enrolled. You'll work on practice projects and available assignments — free to join, with a clear path to building real experience.</p>
            <ul class="path-features">
              <li>Free — no cost to students</li>
              <li>Practice projects and available assignments</li>
              <li>Portfolio and resume-ready work</li>
              <li>Apply when applications open</li>
            </ul>
            <RctButton variant="ghost" href="#notify" class="path-cta">Register Interest</RctButton>
          </div>
        </div>
      </div>
    </section>

    <!-- NOTIFY / CONTACT -->
    <section class="contact-section" id="notify">
      <div class="section-inner">
        <p class="section-label reveal">Stay In The Loop</p>
        <h2 class="section-heading reveal reveal-delay-1">Now accepting interest.</h2>
        <div class="contact-layout">
          <div class="contact-aside reveal reveal-delay-2">
            <p>The program infrastructure is built and the first cohort is being planned. Whether you're a student looking for real engineering experience or a department exploring a partnership — register your interest now and we'll reach out when applications open.</p>
            <div class="contact-detail">
              <a href="mailto:hello@ridgecodetech.com">hello@ridgecodetech.com</a>
              <a href="https://ridgecodetech.com">ridgecodetech.com</a>
            </div>
          </div>
          <div class="reveal reveal-delay-3">
            <form class="contact-form" novalidate @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">Name</label>
                  <input v-model="formName" type="text" id="name" name="name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="email">Email</label>
                  <input v-model="formEmail" type="email" id="email" name="email" placeholder="you@university.edu" required />
                </div>
              </div>
              <div class="form-group">
                <label for="service">I am a…</label>
                <select v-model="formService" id="service" name="service" required>
                  <option value="" disabled>Select your role</option>
                  <option value="Student — Register Interest">Student — register my interest</option>
                  <option value="Institutional Partnership Inquiry">Department / Institution — partnership inquiry</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">Message</label>
                <textarea v-model="formMessage" id="message" name="message" placeholder="Tell us about your institution, program size, or questions about the internship..." required></textarea>
              </div>
              <div v-if="formStatus.msg" :class="['form-status', formStatus.type]">{{ formStatus.msg }}</div>
              <RctButton type="submit" :loading="formLoading" :disabled="formLoading">Send Message</RctButton>
            </form>
          </div>
        </div>
      </div>
    </section>

    <AgentChat scope="intern" :agent-url="AGENT_URL" />

    <RctFooter :links="footerLinks">
    </RctFooter>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { RctNavbar, RctButton, RctFooter, AgentChat } from '@rct/ui';
import { FOOTER_LINKS, WORKER_URL, AGENT_URL, isValidEmail } from '@rct/shared';
import { useAuth } from '../composables/useAuth';

const { isAuthenticated } = useAuth();

const navLinks = computed(() => [
  { href: '#how-it-works', label: 'How It Works' },
  { href: '#projects',     label: 'Projects'     },
  { href: '#paths',        label: 'Enroll'        },
  { href: '#notify',       label: 'Register'      },
  isAuthenticated.value
    ? { href: '/dashboard', label: 'Dashboard' }
    : { href: '/sign-in',   label: 'Sign In'   },
]);

const footerLinks = [
  { href: 'https://ridgecodetech.com', label: 'Home' },
  ...FOOTER_LINKS.filter((l) => l.href !== 'https://intern.ridgecodetech.com'),
] as { href: string; label: string }[];

const formName    = ref('');
const formEmail   = ref('');
const formService = ref('');
const formMessage = ref('');
const formLoading = ref(false);
const formStatus  = ref<{ type: 'success' | 'error' | ''; msg: string }>({ type: '', msg: '' });

async function handleSubmit() {
  const name    = formName.value.trim();
  const email   = formEmail.value.trim();
  const service = formService.value;
  const message = formMessage.value.trim();

  if (!name || !email || !service || !message) {
    formStatus.value = { type: 'error', msg: 'Please fill in all fields before sending.' };
    return;
  }
  if (!isValidEmail(email)) {
    formStatus.value = { type: 'error', msg: 'Please enter a valid email address.' };
    return;
  }

  formLoading.value = true;
  formStatus.value  = { type: '', msg: '' };

  try {
    const res = await fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, email, service, message }),
    });

    if (res.ok) {
      formStatus.value = { type: 'success', msg: "Got it — we'll reach out when the first cohort opens." };
      formName.value = formEmail.value = formService.value = formMessage.value = '';
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      formStatus.value = { type: 'error', msg: data.error ?? 'Something went wrong. Please try emailing directly.' };
    }
  } catch {
    formStatus.value = { type: 'error', msg: 'Network error — please email hello@ridgecodetech.com directly.' };
  } finally {
    formLoading.value = false;
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
});
</script>

<style>
@keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }

.hero { min-height: 100vh; background: var(--navy-deep); display: flex; flex-direction: column; justify-content: center; padding: 10rem 4rem 0; position: relative; overflow: hidden; }
.hero::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(ellipse 60% 50% at 70% 30%, rgba(122,150,193,0.1) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 20% 70%, rgba(15,38,79,0.8) 0%, transparent 60%); pointer-events: none; }
.hero-content { position: relative; max-width: 820px; }
.coming-soon-badge { display: inline-block; font-family: 'Space Grotesk', sans-serif; font-size: 0.65rem; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: var(--periwinkle); border: 1px solid rgba(190,205,234,0.3); background: rgba(122,150,193,0.1); padding: 0.35rem 0.75rem; border-radius: 2px; margin-bottom: 1.75rem; opacity: 0; animation: fadeUp 0.7s 0.1s cubic-bezier(.4,0,.2,1) forwards; }
.hero-headline { font-family: 'Space Grotesk', sans-serif; font-size: clamp(2.8rem, 6vw, 5rem); font-weight: 700; line-height: 1.08; color: var(--white); letter-spacing: -0.02em; margin-bottom: 1.75rem; opacity: 0; animation: fadeUp 0.7s 0.3s cubic-bezier(.4,0,.2,1) forwards; }
.hero-headline em { font-style: normal; color: var(--periwinkle); }
.hero-sub { font-size: 1.15rem; font-weight: 300; color: var(--periwinkle); line-height: 1.7; max-width: 560px; margin-bottom: 3rem; opacity: 0; animation: fadeUp 0.7s 0.5s cubic-bezier(.4,0,.2,1) forwards; }
.hero-actions { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; opacity: 0; animation: fadeUp 0.7s 0.7s cubic-bezier(.4,0,.2,1) forwards; }

.ridge-divider { position: relative; width: 100%; height: 120px; background: var(--navy-deep); margin-top: 6rem; }
.ridge-divider svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }

section { padding: 6rem 4rem; }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-label { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.22em; text-transform: uppercase; color: var(--steel); margin-bottom: 1rem; }
.section-heading { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.75rem, 3vw, 2.5rem); font-weight: 700; letter-spacing: -0.02em; color: var(--navy); line-height: 1.2; margin-bottom: 1.5rem; }
.section-body { font-size: 1rem; font-weight: 300; color: var(--text-muted); line-height: 1.8; max-width: 600px; margin-bottom: 0; }

.how-section { background: var(--off-white); }
.steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; margin-top: 3.5rem; border: 2px solid var(--periwinkle); }
.step { padding: 2.5rem 2rem; background: var(--white); border-right: 2px solid var(--periwinkle); }
.step:last-child { border-right: none; }
.step-number { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.15em; color: var(--steel); opacity: 0.6; margin-bottom: 1rem; }
.step-title { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; font-weight: 600; color: var(--navy); margin-bottom: 0.75rem; }
.step-desc { font-size: 0.875rem; line-height: 1.7; color: var(--text-muted); font-weight: 300; }

.work-section { background: var(--navy); padding: 6rem 4rem; }
.work-section .section-label { color: var(--steel); }
.work-section .section-heading { color: var(--pale); }
.work-section .section-body { color: var(--periwinkle); margin-bottom: 3rem; }
.work-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; border: 2px solid var(--navy-mid); margin-top: 3rem; }
.work-card { background: rgba(255,255,255,0.03); border-right: 2px solid var(--navy-mid); padding: 2.5rem 2rem; transition: background 0.2s; }
.work-card:last-child { border-right: none; }
.work-card:hover { background: rgba(122,150,193,0.06); }
.work-icon { width: 40px; height: 40px; margin-bottom: 1rem; opacity: 0.7; }
.work-tag { display: inline-block; font-family: 'Space Grotesk', sans-serif; font-size: 0.6rem; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; padding: 0.25rem 0.6rem; border-radius: 2px; margin-bottom: 1rem; }
.institutional-tag { color: var(--periwinkle); border: 1px solid rgba(190,205,234,0.25); background: rgba(122,150,193,0.12); }
.both-tag { color: var(--steel); border: 1px solid var(--navy-mid); background: rgba(255,255,255,0.04); }
.work-title { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; font-weight: 600; color: var(--pale); margin-bottom: 0.75rem; }
.work-desc { font-size: 0.875rem; line-height: 1.7; color: var(--periwinkle); font-weight: 300; }

.paths-section { background: var(--off-white); }
.paths-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px; margin-top: 3rem; border: 2px solid var(--periwinkle); }
.path-card { padding: 3rem 2.5rem; background: var(--white); position: relative; }
.path-institutional { border-right: 2px solid var(--periwinkle); background: var(--pale); }
.path-recommended { font-family: 'Space Grotesk', sans-serif; font-size: 0.65rem; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; color: var(--navy); background: var(--periwinkle); padding: 0.3rem 0.7rem; border-radius: 2px; display: inline-block; margin-bottom: 1.25rem; }
.path-title { font-family: 'Space Grotesk', sans-serif; font-size: 1.25rem; font-weight: 700; color: var(--navy); margin-bottom: 0.35rem; }
.path-audience { font-size: 0.8rem; color: var(--steel); font-weight: 400; margin-bottom: 1.25rem; font-family: 'Space Grotesk', sans-serif; letter-spacing: 0.05em; }
.path-desc { font-size: 0.9rem; line-height: 1.75; color: var(--text-muted); font-weight: 300; margin-bottom: 1.75rem; }
.path-features { list-style: none; display: flex; flex-direction: column; gap: 0.6rem; margin-bottom: 2rem; }
.path-features li { font-size: 0.85rem; color: var(--text-body); padding-left: 1.25rem; position: relative; line-height: 1.5; }
.path-features li::before { content: '✓'; position: absolute; left: 0; color: var(--steel); font-size: 0.75rem; }

.contact-section { background: var(--navy-deep); padding: 7rem 4rem; }
.contact-section .section-label { color: var(--steel); }
.contact-section .section-heading { color: var(--pale); }
.contact-layout { display: grid; grid-template-columns: 1fr 1.4fr; gap: 5rem; align-items: start; margin-top: 3rem; }
.contact-aside p { font-size: 0.9rem; color: var(--periwinkle); line-height: 1.8; font-weight: 300; margin-bottom: 2rem; }
.contact-detail { display: flex; flex-direction: column; gap: 0.75rem; }
.contact-detail a { font-family: 'Space Grotesk', sans-serif; font-size: 0.875rem; color: var(--steel); text-decoration: none; border-bottom: 1px solid var(--navy-mid); padding-bottom: 2px; width: fit-content; transition: color 0.2s, border-color 0.2s; }
.contact-detail a:hover { color: var(--pale); border-color: var(--steel); }
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; color: var(--steel); }
.form-group input, .form-group select, .form-group textarea { font-family: 'Inter', sans-serif; font-size: 0.9rem; font-weight: 300; color: var(--pale); background: rgba(255,255,255,0.05); border: 1px solid var(--navy-mid); border-radius: 3px; padding: 0.8rem 1rem; outline: none; transition: border-color 0.2s, background 0.2s; width: 100%; }
.form-group select option { background: var(--navy-deep); color: var(--pale); }
.form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--steel); background: rgba(255,255,255,0.08); }
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder, .form-group textarea::placeholder { color: var(--text-muted); }
.form-status { font-size: 0.875rem; padding: 0.75rem 1rem; border-radius: 3px; }
.form-status.success { background: rgba(122,150,193,0.15); border: 1px solid var(--steel); color: var(--pale); }
.form-status.error   { background: rgba(180,60,60,0.15); border: 1px solid #b44; color: #f5a0a0; }

@media (max-width: 900px) {
  .steps-grid   { grid-template-columns: 1fr; }
  .step         { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .step:last-child { border-bottom: none; }
  .work-grid    { grid-template-columns: 1fr; }
  .work-card    { border-right: none; border-bottom: 2px solid var(--navy-mid); }
  .work-card:last-child { border-bottom: none; }
  .paths-grid   { grid-template-columns: 1fr; }
  .path-institutional { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .contact-layout { grid-template-columns: 1fr; gap: 3rem; }
}
@media (max-width: 768px) {
  section { padding: 4rem 1.5rem; }
  .hero   { padding: 8rem 1.5rem 0; }
  .form-row { grid-template-columns: 1fr; }
  .contact-section { padding: 4rem 1.5rem; }
  .work-section    { padding: 4rem 1.5rem; }
}
</style>
